// Libraries
import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
} from '@chakra-ui/react';
// Components
import { FormSubmit, ResetIcon } from '@atoms/index';
// Utils
import text from '@translations/en.json';
// Types
import { Fragment } from 'react';
import { TableFiltersDrawerProps } from './TableFiltersDrawer.types';

/**
 * Drawer component used to display the table filters on smaller views.
 *
 * @component
 * @param {TableFiltersDrawerProps} props - The props for the TableFiltersDrawer component.
 * @param {number} appliedFilters The count of the currently applied filters.
 * @param {() => void} clearFilters function to call clear all the filters
 * @param {boolean} props.isOpen - Flag indicating whether the drawer is open or not.
 * @param {Function} props.onClose - Callback function to be called for open or clone the drawer.
 * @returns {ReactElement} The TableFiltersDrawer component.
 */
export const TableFiltersDrawer = ({
  isOpen,
  onClose,
  appliedFilters,
  clearFilters,
  children,
}: TableFiltersDrawerProps) => {
  const resultsText = appliedFilters > 0 ? `${text.update_results} (${appliedFilters})` : text.update_results;
  return (
    <Drawer isOpen={isOpen} placement='right' onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent maxW={{ base: 'full', sm: 80 }}>
        <DrawerHeader p={0}>
          <Flex px={5} py={3.5} align='center'>
            <Text flex={1} fontSize='l' fontWeight='semibold' textAlign='center'>
              {text.filters}
            </Text>
            <IconButton
              p={2}
              h={8}
              minW={8}
              aria-label={text.close_menu}
              variant='baseLight'
              icon={<CloseIcon color='text.faded' />}
              onClick={onClose}
            />
          </Flex>
        </DrawerHeader>
        <DrawerBody display='flex' flexDirection='column' p={0}>
          <Fragment>
            {children}
            <Flex w='full' justify='space-between' flex={1} px={5} py={6} gap={5}>
              <Button w='full' h={9} type='button' onClick={clearFilters} variant='light'>
                <Flex alignItems='center' columnGap={2}>
                  <ResetIcon boxSize={3} color='inherit' />
                  <Text fontSize='m' color='inherit'>
                    {text.reset_filters}
                  </Text>
                </Flex>
              </Button>
              {/* TODO: We need to create a real buttons palette and start using them */}
              <FormSubmit w='full' h={9} fontSize='s' fontWeight='medium' onClick={onClose}>
                {resultsText}
              </FormSubmit>
            </Flex>
          </Fragment>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
